import "isomorphic-fetch";
import { browserHistory } from "react-router";
import _ from "underscore";
import { isBrowser } from "../../../core/detection";
import { RoutesInvoker } from "../../../web/routesInvoker";
import { CustomErrors } from "../../errors";

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

/**
 * Sample implementation of IApiClient using ES6 fetch
 */
export class Fetcher {

  /**
   * Send a request to the Api via 'fetch'
   * @param method The Http Method
   * @param url The Request Url
   * @param payload The Request Payload
   * @param authToken The authorization bearer token
   */
  public static sendJson<T, R>(method: HttpMethod, url: string, payload?: T, authToken: string = null, currencyCode: string = null, isPrimary: boolean = false): Promise<IFetchApiResponse<R>> {

    const p = payload ? JSON.stringify(payload) : null;
    const init: any = Fetcher.requestInit(method, p, "application/json");
    if (authToken) {
      Fetcher.addAuthHeader(init, authToken);
    }
    if (currencyCode) {
      Fetcher.addCurrencyHeader(init, currencyCode);
    }

    return Fetcher.fetchJson<R>(url, init, isPrimary);
  }

  /**
   * Send a form encoded request to the Api via 'fetch'
   * @param method The Http Method
   * @param url The Request Url
   * @param payload The Request Payload
   * @param authToken The authorization bearer token
   */
  public static sendForm<T, R>(method: string, url: string, payload?: T, authToken: string = null): Promise<IFetchApiResponse<R>> {
    const p = payload ? Fetcher.postFormPayload(payload) : undefined;
    const init: any = Fetcher.requestInit(method, p, "application/x-www-form-urlencoded");
    if (authToken) {
      Fetcher.addAuthHeader(init, authToken);
    }

    return Fetcher.fetchJson<R>(url, init, false);
  }

  private static getContent<T>(r: Response): Promise<IFetchApiContent<T>> {
    const contentType = r.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") > -1) {
      return r.json().then((json) => {
        return { json };
      });
    }

    if (contentType && contentType.indexOf("text/html") > -1) {
      return r.text().then((text: string) => {
        return { text };
      });
    }

    return Promise.resolve({});
  }

  private static fetchJson<R>(url: string, init: any, isPrimary: boolean): Promise<IFetchApiResponse<R>> {
    return (fetch(url, init) as Promise<Response>).then(r => {
      return Fetcher.getContent<R>(r).then(content => {
        const response: IFetchApiResponse<R> = { response: r };
        if (content.text) {
          response.text = content.text;
        }
        if (content.json) {
          response.json = content.json;
        }
        
        if(isPrimary) {
          response.response.isPrimary = true;
        }

        if (r.status >= 500) {
          throw new CustomErrors.ApiError("Server Error", response);
        }
        if (r.status === 401) {
          if (isBrowser()) {
            browserHistory.push(RoutesInvoker.login("We need you to sign in again to prove your identity."));
          }
          throw new CustomErrors.ApiError("Unauthorized Error", response);
        }
        if (r.status >= 400) {
          throw new CustomErrors.ApiError("Client Error", response);
        }
        return response;
      });
    }).catch<IFetchApiResponse<R>>(err => {
      if (err.name && err.name === "ApiError") {
        return Promise.reject(err);
      }
      const msg = "Network Error: " + url;
      const apiErr = new CustomErrors.ApiError(err.message, { response: { status: 0, isPrimary }, text: msg });
      return Promise.reject(apiErr);
    });
  }

  private static addAuthHeader(init: any, token: string) {
    init.headers.Authorization = `Bearer ${token}`;
  }

  private static addCurrencyHeader(init: any, currencyCode: string) {
    init.headers["Accept-Currency"] = currencyCode;
  }

  private static postFormPayload<T>(entity: T): string {
    return _.reduce<string, string>(_.keys(entity), (m, key) => {
      const item = `${key}=${encodeURIComponent(entity[key])}`;
      if (m) {
        return `${m}&${item}`;
      }
      return item;
    }, "");
  }

  private static requestInit(method: string = "GET", payload: string = null, contentType?: string): any {
    const init: any = {
      method,
      headers: {
        "Content-Type": contentType,
      },
    };
    if (payload) {
      init.body = payload;
    }

    return init;
  }
}
